import { DefaultPrivacyLevel } from '@datadog/browser-rum';
import type { RumInitConfiguration } from '@datadog/browser-rum';

import type { DatadogRumConfig } from '@/shared/types/datadogRum';

const DEFAULT_SITE = 'us5.datadoghq.com';

const validSites = new Set<NonNullable<RumInitConfiguration['site']>>([
  'datadoghq.com',
  'us3.datadoghq.com',
  'us5.datadoghq.com',
  'datadoghq.eu',
  'ddog-gov.com',
  'ap1.datadoghq.com',
]);

function parseOptionalBoolean(value: string | undefined, fallbackValue: boolean): boolean {
  if (value === undefined) {
    return fallbackValue;
  }

  return value === 'true';
}

function parseOptionalNumber(value: string | undefined, fallbackValue: number): number {
  if (!value) {
    return fallbackValue;
  }

  const parsedValue = Number(value);

  if (!Number.isFinite(parsedValue)) {
    return fallbackValue;
  }

  return parsedValue;
}

function parseSite(value: string | undefined): DatadogRumConfig['site'] {
  if (!value) {
    return DEFAULT_SITE;
  }

  return validSites.has(value as NonNullable<RumInitConfiguration['site']>)
    ? (value as DatadogRumConfig['site'])
    : DEFAULT_SITE;
}

export function getDatadogRumConfig(): DatadogRumConfig | null {
  const isEnabled = parseOptionalBoolean(process.env.NEXT_PUBLIC_DATADOG_RUM_ENABLED, true);
  const applicationId = process.env.NEXT_PUBLIC_DATADOG_RUM_APPLICATION_ID?.trim() ?? '';
  const clientToken = process.env.NEXT_PUBLIC_DATADOG_RUM_CLIENT_TOKEN?.trim() ?? '';

  if (!isEnabled || !applicationId || !clientToken) {
    return null;
  }

  return {
    applicationId,
    clientToken,
    site: parseSite(process.env.NEXT_PUBLIC_DATADOG_RUM_SITE),
    service:
      process.env.NEXT_PUBLIC_DATADOG_RUM_SERVICE?.trim() ??
      process.env.NEXT_PUBLIC_DATADOG_SERVICE?.trim() ??
      'arbitralis-cumprimento-frontend',
    env: process.env.NEXT_PUBLIC_DATADOG_RUM_ENV?.trim() ?? process.env.NODE_ENV ?? 'development',
    version: process.env.NEXT_PUBLIC_DATADOG_RUM_VERSION?.trim() ?? '0.1.0',
    sessionSampleRate: parseOptionalNumber(
      process.env.NEXT_PUBLIC_DATADOG_RUM_SESSION_SAMPLE_RATE,
      100,
    ),
    sessionReplaySampleRate: parseOptionalNumber(
      process.env.NEXT_PUBLIC_DATADOG_RUM_SESSION_REPLAY_SAMPLE_RATE,
      20,
    ),
    trackResources: parseOptionalBoolean(process.env.NEXT_PUBLIC_DATADOG_RUM_TRACK_RESOURCES, true),
    trackUserInteractions: parseOptionalBoolean(
      process.env.NEXT_PUBLIC_DATADOG_RUM_TRACK_USER_INTERACTIONS,
      true,
    ),
    trackLongTasks: parseOptionalBoolean(
      process.env.NEXT_PUBLIC_DATADOG_RUM_TRACK_LONG_TASKS,
      true,
    ),
    defaultPrivacyLevel: DefaultPrivacyLevel.MASK_USER_INPUT,
  };
}
